/**
 * Shared configuration for the public "book a demo" flow.
 *
 * The calendar link is interpolated as `https://cal.com/${DEMO_CALENDAR_LINK}` by the
 * DemoModal renderer, so this value must be the cal.com slug, NOT a full URL.
 */

/** Cal.com slug for the public "book a demo" calendar. */
export const DEMO_CALENDAR_LINK = "nowadays/30min";

/**
 * cal.com booking-question identifiers for the nowadays/30min event type, used to prefill the
 * calendar form from the demo intake so prospects don't re-type their answers.
 *
 * Find each in cal.com → Event Types → 30 Min → Advanced → Booking questions → the question's
 * "Identifier" field. Leave a value as "" to skip prefilling that field (name/email/notes always
 * prefill via cal.com's built-in params regardless of this map).
 */
export const CAL_BOOKING_FIELD_IDENTIFIERS = {
  employeeCount: "Number-of-employees-at-your-company",
  eventsPerYear: "Number-of-events-per-year",
  // Budget is mapped to the event type's built-in "title" field (renamed to the budget question).
  budgetRange: "title",
  // The modal now asks about "20+" rooms, but the cal.com booking question's identifier still reads
  // "10" — updating the cal.com dashboard question text/identifier to "20" is a manual follow-up.
  needs20Rooms: "Do-your-events-require-10--hotel-guest-rooms-on-peak-night",
} as const;

/** Company-size options for the demo intake. Each tier starts on a round number and ends one below the next. */
export const EMPLOYEE_BRACKETS = ["1-49", "50-199", "200-999", "1,000-4,999", "5,000+"] as const;

/** A single company-size option from {@link EMPLOYEE_BRACKETS}. */
export type EmployeeBracket = (typeof EMPLOYEE_BRACKETS)[number];

/**
 * Employee-count brackets that fail the demo qualification gate (fewer than 200 employees, see ENG-26).
 * Everything else in EMPLOYEE_BRACKETS clears the 200+ threshold, so new larger brackets qualify
 * automatically. Typed against EMPLOYEE_BRACKETS so renaming a bracket there surfaces as a compile
 * error here instead of silently letting small-company leads through the gate.
 */
export const DISQUALIFYING_EMPLOYEE_BRACKETS: readonly EmployeeBracket[] = ["1-49", "50-199"];

/**
 * Whether a demo prospect qualifies for the calendar vs. the out-of-scope waitlist (ENG-26).
 * A prospect qualifies only with 200 or more employees AND 20+ hotel guest rooms on peak night.
 *
 * @param employeeCount - Selected company-size bracket (free-form string from form state).
 * @param needs20Rooms - Whether the prospect needs 20+ hotel guest rooms on peak night.
 * @returns `true` when the prospect passes both gate criteria.
 */
export function isDemoQualified(employeeCount: string, needs20Rooms: boolean): boolean {
  return needs20Rooms && !(DISQUALIFYING_EMPLOYEE_BRACKETS as readonly string[]).includes(employeeCount);
}

/** Shown to leads who don't pass the demo qualification gate (see ENG-26). */
export const DEMO_OUT_OF_SCOPE_MESSAGE =
  "Thanks for sharing all the details about your event! Right now Nowadays is focused on larger group programs, but we're planning to open up to more event types in August 2026. We'll keep you posted when we do :)";

/** Typical event budget range options for the demo intake. */
export const BUDGET_BRACKETS = ["Under $25k", "$25k-$100k", "$100k-$250k", "$250k-$1M", "$1M+"] as const;
